import React from "react";
import _ from "lodash";
import {connect} from 'react-redux'

import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";

import {toGermanFormat} from "../../../../../../../../../utils/numberFormater";
import DashboardTable from "../../../../../../../../../components/DashboardTable/DashboardTable";
import {
  tableInvestmentStrategyStructure,
  tableStructure,
  tableStructureEinzeltitel,
  tableStructureVL
} from "./table-data";

import PrimaryButton from "../../../../../../../../../components/Buttons/PrimaryButton";
import SelectFormElement from "../../../../formElement/SelectFormElement";

import {
  instrumentDiscountPath,
  INSTRUMENTS_KEY,
  SAVINGS_PLANS_KEY,
  SINGLE_INVESTMENTS_KEY
} from "../../../../../../../mock_produktauswahl";
import {getVatCoef, isRequiredQuestion} from "../../../../../../../utils";
import InputFormElement, {
  DECIMAL_SCALE,
  PercentageNumberFormat
} from '../../../../formElement/InputFormElement/InputFormElement'
import {withPercentOrDash} from "../../../../../../../../../utils/utils";
import {newDesignInputCustomClasses, OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG} from "../../../../../../../constants";
import SafeHtml from "../../../../../../../../../components/SafeHtml";
import {DisabledReasonTooltip} from "../../../../../../../../../components/Tooltip";
import WarningTooltip from "../../../../../../../../../components/WarningTooltip";
import InfoCard from "../../../DynamicStep/components/InfoCard";
import {getInvestmentDynamicPath, getRouteFullPathByComponent} from "../../../../../../../../InvestmentPlatform/utils";
import {getInvestmentPlatformSelector} from "../../../../../../../../../utils/redaxSelectors";

import styles, {useEinzeltitelTableStyles, useTableStyles, useStyles} from "./styles";
import {calculateServiceFeeVAT} from "../../../ProductsSelectionSteps/components/SummaryStep/utils";
import WarningIcon from "../../../../../../../../../components/Icons/WarningIcon";

const COMMON_MAX_SERVICE_FEE = 2; // in case service fee is higher we will show info message
export const MISSING_INSTRUMENTS_MSG = 'Es wurde noch kein Produkt ausgewählt. Der Anlageplan ist erst vollständig, wenn die Anlageerstellung ergänzt wurde.';

const SummaryStepV2 = props => {
  const {
    classes,
    dataService,
    questions: [
      discountsQuestion,
      kickbacksQuestion,
      serviceFeeQuestion,
      serviceFeeVatQuestion
    ],
    onAnswerChange
  } = props;

  const tableClasses = useTableStyles();
  const einzeltitleTableClasses = useEinzeltitelTableStyles();

  const stepsData = dataService.step.stepsData;
  const vatCoef = React.useMemo(() => {
    return getVatCoef();
  }, []);

  const isModelPortfolio = _.get(stepsData, 'modelPortfolio.is_modelportfolio');

  React.useEffect(() => {
    // serviceFeeVat should be recalculated as it depends on serviceFee
    onAnswerChange(serviceFeeVatQuestion.uid, calculateServiceFeeVAT(serviceFeeQuestion.answer, vatCoef))
  }, [serviceFeeQuestion.answer]);

  React.useEffect(() => {
    serviceFeeQuestion.minValue = isModelPortfolio && kickbacksQuestion.answer == 100 && 0.2;
  }, [kickbacksQuestion.answer, isModelPortfolio]);

  const modelPortfolioDetailsPath = React.useMemo(() => {
    if(isModelPortfolio){
      return `${window.location.origin}/${getInvestmentDynamicPath()}${getRouteFullPathByComponent(props.investmentPlatform.routes, 'MODELPORTFOLIO_OVERVIEW', ':id')}`;
    }
  }, [isModelPortfolio]);

  const isInvestmentStrategy = _.get(stepsData, 'modelPortfolio.isInvestmentStrategy');

  const allowedDiscountTypes = _.get(stepsData, 'custodian.depot.onboarding_discounts_allowed') || [];

  const isInstrumentDiscountPossible = (isin) => {
    const instruments = stepsData.instruments[0] || [];
    const instrumentData = _.find(instruments, (instrument) => instrument.isin == isin);
    if (!instrumentData) {
      return false
    }

    return instrumentData.is_fund;
  };

  const changeDiscount = (field, value) => {

    const discountsData = {...discountsQuestion.answer};

    function setForAllInstruments(key){
      Object.keys(discountsData[INSTRUMENTS_KEY] && discountsData[INSTRUMENTS_KEY][key] || {}).forEach(isin => {
        if (isInstrumentDiscountPossible(isin)) {
          _.set(discountsData, [INSTRUMENTS_KEY, key, isin, 'discount'], value);
        }
      });
    }

    if(field === 'overall'){
      discountsData[field] = value;

      if (!stepsData.modelPortfolio && !stepsData.combinedDiscounts) {
        [SINGLE_INVESTMENTS_KEY, SAVINGS_PLANS_KEY].forEach(key => {
          discountsData[key] = value;
          setForAllInstruments(key);
        });
      }

    } else {
      discountsData[field] = value;
      setForAllInstruments(field);
    }

    onAnswerChange(discountsQuestion.uid, discountsData)
  };

  const changeItemDiscount = (instrument, value) => {

    if (!isInstrumentDiscountPossible(instrument.isin, instrument.savingsPlan ? SAVINGS_PLANS_KEY : SINGLE_INVESTMENTS_KEY)) {
      return false
    }

    const discountsData = {...discountsQuestion.answer};
    _.set(discountsData, instrumentDiscountPath(instrument), value);

    onAnswerChange(discountsQuestion.uid, discountsData)
  };

  const onEditDiscountsClick = () => {
    if(discountsQuestion.answer.discountsEnabled) {
      discountsQuestion.answer.discountsEnabled = false;
      changeDiscount('overall', ''); // clear all values
    } else {
      onAnswerChange(discountsQuestion.uid, {...discountsQuestion.answer, discountsEnabled: true});
    }
  };

  const handleKickbackChange = (value) => {
    onAnswerChange(kickbacksQuestion.uid, value);
  };

  const updateServiceFeeQuestionAnswer = (value) => {
    onAnswerChange(serviceFeeQuestion.uid, value >= 0 ? value : 0 || 0);
  };

  const topArrowClicked = () => {
    updateServiceFeeQuestionAnswer(_.round(parseFloat(serviceFeeQuestion.answer) + 0.1, DECIMAL_SCALE))
  };

  const bottomArrowClicked = () => {
    updateServiceFeeQuestionAnswer(_.round(parseFloat(serviceFeeQuestion.answer) - 0.1, DECIMAL_SCALE))
  };

  const showKickback = !_.isEmpty(kickbacksQuestion.config.choices);
  const showServiceFee = !_.isNil(serviceFeeQuestion.maxValue) || !_.isNil(serviceFeeQuestion.minValue);
  const investmentStrategyTotalCommission = isInvestmentStrategy && _.sum([
    _.get(stepsData, 'modelPortfolio.bfv_commission'),
    _.get(stepsData, 'modelPortfolio.asset_manager_commission'),
    serviceFeeQuestion.answer
  ]);

  const depotDiscountDisabled = !allowedDiscountTypes.includes('depot');

  const availableDiscounts = !stepsData.einzeltitelsOnly && discountsQuestion.config.choices || [];
  let availableOverAllDiscounts = availableDiscounts;
  // for assets - overall discount can't be higher then MIN of instruments max_discount
  if(!stepsData.modelPortfolio) {
    availableOverAllDiscounts = availableOverAllDiscounts.filter(o => parseInt(o.value || '0') <= stepsData.maxFundsInstrumentsOverallDiscount)
  }
  const showDiscounts = !_.isEmpty(availableOverAllDiscounts);

  let serviceFeeInfoText, serviceFeeQuestionRequired = isRequiredQuestion(serviceFeeQuestion);
  if (isInvestmentStrategy){
    serviceFeeInfoText = "Sie können nur den “Anteil Vertrieb p.a. (zzgl. MwSt.)” rabattieren. Weitere Details zur Gesamtvergütung finden Sie unten."
  } else if(isModelPortfolio){
    serviceFeeInfoText = serviceFeeQuestion.minValue && `Wenn Sie eine Rückerstattung der Bestandsprovision angeben haben, müssen Sie mindestens ${toGermanFormat(serviceFeeQuestion.minValue)}% Serviceentgelt angeben.`;
  } else {
    serviceFeeInfoText = serviceFeeQuestion.answer > COMMON_MAX_SERVICE_FEE && `Ihr Eingabe der Servicefee überschreitet die übliche maximale Höhe von ${COMMON_MAX_SERVICE_FEE}%. Bitte überprüfen Sie Ihre Eingabe.`
  }

  const instrumentsMissing = stepsData.instruments[0].length === 0;

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} className={classes.pageTitle}>
        Anlageplan für die Investmentanlage Ihres Kunden/Ihrer Kundin
      </Grid>
      {instrumentsMissing && (
        <Grid item xs={12}>
          <InfoCard isNewDesign cardType={'warning'} info_text={MISSING_INSTRUMENTS_MSG} />
        </Grid>
      )}
      {!_.isEmpty(stepsData.custodian.depot) && !_.isEmpty(stepsData.custodian.contract) && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <DepotCostSection
              custodian={stepsData.custodian}
              isInvestmentStrategy={isInvestmentStrategy}
              investmentStrategyTotalCommission={investmentStrategyTotalCommission}
              vatCoef={vatCoef}
            />
            <Grid item xs={12} md={6} className={classes.stepInfoContainer}>
              <Grid container spacing={2}>
                {!!isInvestmentStrategy && (
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs className={classes.stepInfoContainer}>
                        <div className={classes.stepInfoTitle}>Anteil Vermögensverwalter</div>
                        <div>{toGermanFormat(stepsData.modelPortfolio.bfv_commission)} p.a. (zzgl. MwSt.)</div>
                        <div>{toGermanFormat(stepsData.modelPortfolio.bfv_commission * vatCoef)} p.a. (inkl. MwSt.)</div>
                      </Grid>
                      <Grid item xs className={classes.stepInfoContainer}>
                        <div className={classes.stepInfoTitle}>Anteil Anlageberater</div>
                        <div>{toGermanFormat(stepsData.modelPortfolio.asset_manager_commission)} p.a. (zzgl. MwSt.)</div>
                        <div>{toGermanFormat(stepsData.modelPortfolio.asset_manager_commission * vatCoef)} p.a. (inkl. MwSt.)</div>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <div className={classes.stepInfoTitle} style={{marginBottom: 0}}>Konditionen</div>
                  <DisabledReasonTooltip title={!showServiceFee && OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG} followCursor>
                    <div>
                      <div className={!showServiceFee && classes.disabled}>
                        <div className={classes.serviceInputContainer}>
                          <div>
                            {serviceFeeQuestion.question_text}{serviceFeeQuestionRequired ? <span className={classes.asterisk}>&nbsp;*</span> : ''}{!!serviceFeeInfoText && <WarningTooltip title={serviceFeeInfoText}/>}
                          </div>
                          <div className={classes.blueInputs}>
                            <Tooltip open={Boolean(serviceFeeQuestion.error)} title={serviceFeeQuestion.error} arrow={true} placement="top">
                              <div style={{position: 'relative', top: '10px'}}/>
                            </Tooltip>
                            <div className={classes.serviceFeeQuestionContainer}>
                              <InputFormElement
                                value={serviceFeeQuestion.answer}
                                onChange={(value) => onAnswerChange(serviceFeeQuestion.uid, value)}
                                inputComponent={PercentageNumberFormat}
                                inputProps={{
                                  inputProps: {allowNegative: false, fixedDecimalScale: true},
                                  endAdornment: (
                                    <div style={{textAlign: 'center'}}>
                                      <div onClick={topArrowClicked} className={classes.arrowsButton}>
                                        <i className={'far fa-angle-up'} />
                                      </div>
                                      <div onClick={bottomArrowClicked} className={classes.arrowsButton}>
                                        <i className={'far fa-angle-down'}  />
                                      </div>
                                    </div>
                                  )
                                }}
                                type={'text'}
                                custom_classes={newDesignInputCustomClasses}
                                required={serviceFeeQuestionRequired}
                              />
                            </div>
                          </div>
                        </div>
                        <div className={classes.serviceInputContainer}>
                          <div>
                            {serviceFeeVatQuestion.question_text} {serviceFeeQuestionRequired ? <span className={classes.asterisk}>&nbsp;*</span> : ''}
                          </div>
                          <div className={classes.blueInputs}>
                            <div className={classes.serviceFeeVatInput}>
                              {withPercentOrDash(serviceFeeVatQuestion.answer, false)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </DisabledReasonTooltip>
                  <DisabledReasonTooltip title={!showKickback && OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG} followCursor>
                    <div>
                      <div className={!showKickback && classes.disabled}>
                        <div className={classes.serviceInputContainer}>
                          <div>
                            {kickbacksQuestion.question_text}{!!kickbacksQuestion.tooltip && <WarningTooltip title={kickbacksQuestion.tooltip}/>}
                          </div>
                          <div className={classes.blueInputs}>
                            <SelectFormElement
                              options={kickbacksQuestion.config.choices}
                              value={kickbacksQuestion.answer}
                              onChange={handleKickbackChange}
                              disabled={kickbacksQuestion.disabled}
                              custom_classes={newDesignInputCustomClasses}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </DisabledReasonTooltip>

                  {showDiscounts && (
                    <div className={classes.serviceInputContainer} style={{height: 71}}>
                      <div>
                        <PrimaryButton
                          text={"Anmerkung"}
                          variant={'text'}
                          icon={<i className={`fal ${discountsQuestion.answer.discountsEnabled ? "fa-times" : "fa-plus"}`} />}
                          disabled={isModelPortfolio && parseFloat(_.get(stepsData, 'modelPortfolio.fee_netto')) == 0}
                          onButtonClick={onEditDiscountsClick} />
                      </div>
                      {discountsQuestion.answer.discountsEnabled && (
                        <div className={classes.blueInputs}>
                          <SelectFormElement
                            label={(
                              <span>
                                Depotrabatt<WarningTooltip title={depotDiscountDisabled ? stepsData.depotDiscountDisableReason : stepsData.depotDiscountInfoText}/>
                              </span>
                            )}
                            options={availableOverAllDiscounts}
                            disabled={depotDiscountDisabled}
                            value={discountsQuestion.answer.overall}
                            onChange={(val) => changeDiscount('overall', val)}
                            custom_classes={newDesignInputCustomClasses}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!stepsData.einzeltitelsOnly && (
        <Grid item xs={12} className={discountsQuestion.answer.discountsEnabled ? 'editMode' : ''}>
          <Grid container spacing={2}>
            <Grid item xs={12} className={classes.headerTitle}>
              Produkte & Einzahlungen
            </Grid>
            {stepsData.infoText.map((infoText, idx) => (
              <Grid item xs={12}><InfoCard uid={idx} info_text={infoText} isNewDesign /></Grid>
            ))}
            {!!stepsData.modelPortfolio && (
              <Grid item xs={12}>
                <DashboardTable
                  structure={tableInvestmentStrategyStructure}
                  dataSource={[stepsData.modelPortfolio]}
                  expanded
                  tableClasses={tableClasses}
                  withFooter={!!isInvestmentStrategy}
                  withInputs
                  options={{
                    onDiscountChange: changeDiscount,
                    discounts: discountsQuestion.answer,
                    availableDiscounts,
                    availableOverAllDiscounts,
                    allowedDiscountTypes,
                    itemsLength: 1,
                    entryFeeInfoText: stepsData.entryFeeInfoText,
                    modelPortfolioDetailsPath
                  }}
                />
              </Grid>
            )}
            {!isInvestmentStrategy && (
              <Grid item xs={12}>
                <DashboardTable
                  structure={stepsData.isVlFlow ? tableStructureVL : tableStructure}
                  dataSource={stepsData.fundsInstruments}
                  expanded
                  tableClasses={tableClasses}
                  withInputs
                  emptyDataMessage={
                    <div style={{textAlign: 'left'}}>
                      <WarningIcon /> <b>Produktauswahl offen</b>
                    </div>
                  }
                  options={{
                    onDiscountChange: changeDiscount,
                    onItemDiscountChange: changeItemDiscount,
                    discounts: discountsQuestion.answer,
                    availableDiscounts,
                    itemsLength: stepsData.fundsInstruments.length,
                    combinedDiscounts: discountsQuestion.answer.combinedDiscounts,
                    isInvestmentStrategy,
                    allowedDiscountTypes,
                    einzeltitelsOnly: false,
                    availableOverAllDiscounts,
                    isModelportfolio: isModelPortfolio
                  }}
                />
              </Grid>
            )}
            {!!stepsData.modelPortfolio && stepsData.modelPortfolio.baseFondTransaction && (
              <BaseFondsTable
                classes={classes}
                tableClasses={tableClasses}
                onAnswerChange={onAnswerChange}
                allowedDiscountTypes={allowedDiscountTypes}
                baseFondTransaction={stepsData.modelPortfolio.baseFondTransaction}
                discountsQuestion={discountsQuestion}
                availableDiscounts={availableDiscounts}
              />
            )}
          </Grid>
        </Grid>
      )}
      {stepsData.einzeltitelInstruments.length > 0 && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} className={classes.headerTitle}>
              Einzeltitel/Aktien
            </Grid>
            <Grid item xs={12}>
              <DashboardTable
                structure={tableStructureEinzeltitel}
                dataSource={stepsData.einzeltitelInstruments}
                expanded
                tableClasses={einzeltitleTableClasses}
                withInputs
                options={{}}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  investmentPlatform: getInvestmentPlatformSelector(state),
});

export const DepotCostSection = ({custodian, isInvestmentStrategy, investmentStrategyTotalCommission, vatCoef}) => {
  const classes = useStyles();

  return (
    <>
      <Grid item xs={12} className={classes.headerTitle}>Depotführung: Kosten & Konditionen</Grid>
      <Grid item xs={12}>
        <div className={classes.depotCard}>
          <Grid container spacing={3} alignItems={'center'}>
            <Grid item xs={'auto'}><img alt={''} src={custodian.depot.logo} /></Grid>
            <Grid item xs>
              <div style={{marginBottom: 8}}><b>Depot / Depotbank</b></div>
              <div>{custodian.contract.label} / {custodian.depot.depot}</div>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={3}>
          <Grid item xs={12} className={classes.stepInfoContainer}>
            <div className={classes.stepInfoTitle}>Depotkosten</div>
            <SafeHtml html={custodian.contract.configuration.depot_cost_display || '-'} />
          </Grid>
          <Grid item xs={12} className={classes.stepInfoContainer}>
            <div className={classes.stepInfoTitle}>Transaktionskosten</div>
            <Grid container spacing={2}>
              <Grid item xs={'auto'} style={{maxWidth: '50%'}}>
                <div>je Kauf/Verkauf</div>
                <SafeHtml html={custodian.contract.configuration.transaction_cost_display_single_buy || '-'} />
              </Grid>
              <Grid item xs>
                <div>Spar-/Entnahmepläne</div>
                <SafeHtml html={custodian.contract.configuration.transaction_cost_display_payment_plan || '-'} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.stepInfoContainer}>
            <div className={classes.stepInfoTitle}>Sonstige Kosten</div>
            <SafeHtml html={custodian.contract.configuration.others_cost_display || '-'} />
          </Grid>
          {!!isInvestmentStrategy && (
            <Grid item xs={12} className={classes.stepInfoContainer}>
              <div className={classes.stepInfoTitle}>Vermögensverwaltungsentgelt</div>
              <div>{toGermanFormat(investmentStrategyTotalCommission)} p.a. (zzgl. MwSt.)</div>
              <div>{toGermanFormat(investmentStrategyTotalCommission * vatCoef)} p.a. (inkl. MwSt.)</div>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  )
}

export default connect(mapStateToProps)(withStyles(styles)(SummaryStepV2));

const BaseFondsTable = ({classes, baseFondTransaction, discountsQuestion, onAnswerChange, allowedDiscountTypes, availableDiscounts, tableClasses}) => {
  const changeItemDiscount = (instrument, value) => {
    const discountsData = {...discountsQuestion.answer};
    _.set(discountsData, instrumentDiscountPath(instrument), value);

    onAnswerChange(discountsQuestion.uid, discountsData)
  };

  const dataSource = React.useMemo(() => {
    return [{
      singleInvestment: baseFondTransaction,
      savingsPlanItem: {
        ...baseFondTransaction,
        savingsPlan: true,
        amount: baseFondTransaction.savingsPlanAmount,
      }
    }]
  }, [baseFondTransaction])

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.headerTitle}>Basisfonds</div>
        </Grid>
        <Grid item xs={12}>
          <DashboardTable
            structure={tableStructure}
            dataSource={dataSource}
            expanded
            tableClasses={tableClasses}
            withInputs
            options={{
              onItemDiscountChange: changeItemDiscount,
              discounts: discountsQuestion.answer,
              availableDiscounts,
              itemsLength: 1,
              combinedDiscounts: discountsQuestion.answer.combinedDiscounts,
              allowedDiscountTypes,
              einzeltitelsOnly: false,
              isBaseFond: true,
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
